import { createCacheWrapper } from '@utils/cache';
import { DAYS_TO_MS, HOURS_TO_MS, configRefs } from '@utils/constants';
import { sitecoreAxios } from './core.axios.js';
import { getPageMetadata } from './request.get-page-metadata.js';
import { TeacherGuideElements } from './request.get-teacher-guide.model.js';
import { buildUrl } from './util.build-url.js';

const cache = createCacheWrapper({
  timeToLiveMs: 1 * HOURS_TO_MS,
  evictAfterMs: 1 * DAYS_TO_MS
});
function getTeacherGuide(portalId, courseId, options) {
  const url = buildUrl(
    `/api/v1/portals/${portalId}/coursefrontpages/${courseId}`,
    options
  );
  return cache(!(options == null ? void 0 : options.disableCache) ? url : void 0, async () => {
    var _a, _b, _c, _d, _e;
    const response = await sitecoreAxios.get(url);
    const metadata = await getPageMetadata(portalId, courseId, options);
    return {
      documents: {
        teacherInstruction: ((_a = response.data.teachersGuide) == null ? void 0 : _a.teacherInstruction) ? `${await configRefs.apis.pm2.media.get()}/${response.data.teachersGuide.teacherInstruction}` : void 0,
        studentAssignments: ((_b = response.data.teachersGuide) == null ? void 0 : _b.studentAssignments) ? `${await configRefs.apis.pm2.media.get()}/${response.data.teachersGuide.studentAssignments}` : void 0,
        extraDocument: ((_c = response.data.teachersGuide) == null ? void 0 : _c.optionalExtraDocument) ? {
          title: response.data.teachersGuide.extraDocumentTitle,
          src: `${await configRefs.apis.pm2.media.get()}/${response.data.teachersGuide.optionalExtraDocument}`
        } : void 0,
        extraDocument2: ((_d = response.data.teachersGuide) == null ? void 0 : _d.optionalExtraDocument2) ? {
          title: response.data.teachersGuide.extraDocumentTitle2,
          src: `${await configRefs.apis.pm2.media.get()}/${response.data.teachersGuide.optionalExtraDocument2}`
        } : void 0
      },
      groups: ((_e = response.data.teachersGuide) == null ? void 0 : _e.contentGroups.map((group) => ({
        id: group.id,
        title: extractGroupTitle(group),
        elements: group.elements.map((element) => extractElement(element, metadata)).filter((it) => !!it)
      }))) ?? []
    };
  }).getStaleWhileRevalidate();
}
function extractGroupTitle(group) {
  var _a;
  if (group.elements.length > 1) {
    return group.title;
  }
  switch ((_a = group.elements[0]) == null ? void 0 : _a.type) {
    case "TeachersGuideMetadata":
      switch (group.elements[0].property) {
        case "educationalDescription":
          return "Om forl\xF8bet";
        case "objectives":
          return "M\xE5l";
        case "formalObjectives":
          return "F\xE6rdigheds- og vidensm\xE5l";
        default:
          return group.title;
      }
    default:
      return group.title;
  }
}
function extractElement(element, metadata) {
  switch (element.type) {
    case "TeachersGuideMetadata":
      switch (element.property) {
        case "objectives":
          return extractObjectivesElement(element, metadata);
        case "formalObjectives":
          return extractFormalObjectivesElement(element, metadata);
        case "educationalDescription":
          return extractEducationalDescriptionElement(element, metadata);
        default:
          console.error(
            "getTeacherGuide.extractElement(): Unable to extract metadata element with requested property: ",
            element.property
          );
          return void 0;
      }
  }
}
function extractObjectivesElement(element, metadata) {
  var _a;
  return {
    id: element.id,
    type: TeacherGuideElements.OBJECTIVES,
    objectives: ((_a = metadata.metadata) == null ? void 0 : _a.objectives.objectives.map((it) => it.text)) ?? []
  };
}
function extractFormalObjectivesElement(element, metadata) {
  var _a;
  const objectiveMap = (_a = metadata.metadata) == null ? void 0 : _a.objectives.formalObjectives.reduce((acc, curr) => {
    var _a2, _b, _c;
    acc[curr.kompetenceomraade] = {
      id: `${curr.kompetenceomraadeId}`,
      title: curr.kompetenceomraade,
      objectives: {
        ...(_a2 = acc[curr.kompetenceomraade]) == null ? void 0 : _a2.objectives,
        [curr.maalPar]: [
          ...((_c = (_b = acc[curr.kompetenceomraade]) == null ? void 0 : _b.objectives) == null ? void 0 : _c[curr.maalPar]) ?? [],
          {
            id: curr.idPair,
            phase: curr.fase,
            fromGrade: curr.fra,
            toGrade: curr.til,
            skillGoal: curr.faerdighedsMaal,
            knowledgeGoal: curr.vidensMaal
          }
        ]
      }
    };
    return acc;
  }, {});
  return {
    id: element.id,
    type: TeacherGuideElements.FORMAL_OBJECTIVES,
    groups: Object.values(objectiveMap ?? {})
  };
}
function extractEducationalDescriptionElement(element, metadata) {
  var _a;
  return {
    id: element.id,
    title: "Om forl\xF8bet",
    type: TeacherGuideElements.DESCRIPTION,
    description: ((_a = metadata.metadata) == null ? void 0 : _a.educationalDescription) ?? ""
  };
}

export { getTeacherGuide };
//# sourceMappingURL=request.get-teacher-guide.js.map
